import React, { useState, useEffect, useRef } from 'react';
import axios from './lib/axiosClient';

const fmt = n => '$' + Number(n||0).toFixed(2);
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : '—';
const fmtSize = b => b > 1024*1024 ? (b/1024/1024).toFixed(1)+' MB' : Math.round(b/1024)+' KB';

export default function ContractManager({ clients = [], showFlash }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected]   = useState(null);  // selected template
  const [sends, setSends]         = useState([]);
  const [loadingSends, setLoadingSends] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showSend, setShowSend]   = useState(false);
  const [replacing, setReplacing] = useState(null);  // template being replaced

  // Upload form
  const [upForm, setUpForm] = useState({ name:'', description:'', is_default:false, requires_signature:true });
  const [upFile, setUpFile] = useState(null);
  const fileRef  = useRef();
  const replRef  = useRef();

  // Send form
  const [sendForm, setSendForm] = useState({ client_id:'', custom_email:'', custom_name:'', message:'' });
  const [sending, setSending]   = useState(false);

  // Send current rental terms (separate from the uploaded-document sends
  // above — this sends whatever's current in Customer Rental Terms)
  const [showSendTerms, setShowSendTerms] = useState(false);
  const [termsSendForm, setTermsSendForm] = useState({ client_id:'', custom_email:'', custom_name:'', message:'' });
  const [sendingTerms, setSendingTerms]   = useState(false);

  useEffect(() => { load(); }, []);
  useEffect(() => { if (selected) loadSends(selected.id); }, [selected]);

  async function load() {
    setLoading(true);
    try {
      const r = await axios.get('/api/rental-business/contracts');
      setTemplates(r.data.templates || []);
      if (!selected && r.data.templates?.length) setSelected(r.data.templates[0]);
    } catch(e) {}
    setLoading(false);
  }

  async function loadSends(id) {
    setLoadingSends(true);
    try {
      const r = await axios.get(`/api/rental-business/contracts/${id}/sends`);
      setSends(r.data.sends || []);
    } catch(e) {}
    setLoadingSends(false);
  }

  async function uploadTemplate() {
    if (!upFile || !upForm.name.trim()) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', upFile);
      fd.append('name', upForm.name);
      fd.append('description', upForm.description);
      fd.append('is_default', upForm.is_default);
      fd.append('requires_signature', upForm.requires_signature);
      const r = await axios.post('/api/rental-business/contracts', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      showFlash?.('Contract uploaded');
      setShowUpload(false);
      setUpForm({ name:'', description:'', is_default:false, requires_signature:true });
      setUpFile(null);
      await load();
      setSelected(r.data.template);
    } catch(e) { showFlash?.(e.response?.data?.error || 'Upload failed', 'error'); }
    setUploading(false);
  }

  async function replaceFile(template, file) {
    setReplacing(template.id);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`/api/rental-business/contracts/${template.id}/replace`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      showFlash?.('Contract replaced — version updated');
      await load();
    } catch(e) { showFlash?.(e.response?.data?.error || 'Replace failed', 'error'); }
    setReplacing(null);
  }

  async function toggleDefault(template) {
    try {
      await axios.put(`/api/rental-business/contracts/${template.id}`, {
        ...template, is_default: !template.is_default
      });
      await load();
    } catch(e) { showFlash?.('Failed', 'error'); }
  }

  async function deleteTemplate(id) {
    if (!window.confirm('Delete this contract template?')) return;
    try {
      await axios.delete(`/api/rental-business/contracts/${id}`);
      showFlash?.('Deleted');
      setSelected(null);
      await load();
    } catch(e) { showFlash?.('Failed', 'error'); }
  }

  async function sendContract() {
    if (!selected) return;
    if (!sendForm.client_id && !sendForm.custom_email) return;
    setSending(true);
    try {
      await axios.post(`/api/rental-business/contracts/${selected.id}/send`, sendForm);
      showFlash?.('Contract sent via email');
      setShowSend(false);
      setSendForm({ client_id:'', custom_email:'', custom_name:'', message:'' });
      loadSends(selected.id);
    } catch(e) { showFlash?.(e.response?.data?.error || 'Send failed', 'error'); }
    setSending(false);
  }

  async function sendCurrentTerms() {
    if (!termsSendForm.client_id && !termsSendForm.custom_email) return;
    setSendingTerms(true);
    try {
      await axios.post('/api/rental-business/contracts/send-current-terms', termsSendForm);
      showFlash?.('Rental terms sent via email');
      setShowSendTerms(false);
      setTermsSendForm({ client_id:'', custom_email:'', custom_name:'', message:'' });
    } catch(e) { showFlash?.(e.response?.data?.error || 'Send failed', 'error'); }
    setSendingTerms(false);
  }

  const S = {
    card:  { background:'#252838', border:'1px solid #2e3150', borderRadius:10, padding:20, marginBottom:16 },
    inp:   { padding:'7px 10px', borderRadius:6, border:'1px solid #3a3f60', background:'#1e2235', color:'#e2e8f0', fontSize:12, width:'100%', boxSizing:'border-box' },
    lbl:   { fontSize:10, color:'#64748b', fontWeight:600, marginBottom:3, display:'block', textTransform:'uppercase', letterSpacing:'.4px' },
    btn:   (color='#f97316') => ({ padding:'7px 16px', borderRadius:7, border:'none', background:color, color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer' }),
    ghost: { padding:'6px 14px', borderRadius:7, border:'1px solid #3a3f60', background:'transparent', color:'#94a3b8', fontSize:12, cursor:'pointer' },
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div style={{ fontSize:18, fontWeight:700, color:'#e2e8f0' }}>📁 Business Documents</div>
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={()=>setShowSendTerms(true)} style={S.btn('#1e3a5f')}>📧 Send Current Rental Terms</button>
          <button onClick={()=>setShowUpload(true)} style={S.btn()}>+ Upload Document</button>
        </div>
      </div>

      {loading && <div style={{ textAlign:'center', padding:40, color:'#4a5568' }}>Loading…</div>}

      {!loading && templates.length === 0 && (
        <div style={{ ...S.card, textAlign:'center', padding:48 }}>
          <div style={{ fontSize:32, marginBottom:12 }}>📁</div>
          <div style={{ fontSize:15, fontWeight:700, color:'#e2e8f0', marginBottom:8 }}>No documents uploaded yet</div>
          <div style={{ fontSize:13, color:'#4a5568', marginBottom:20 }}>Upload other business paperwork (e.g. an equipment-owner agreement) to send by email. Customer rental terms live in the section above.</div>
          <button onClick={()=>setShowUpload(true)} style={S.btn()}>Upload First Document</button>
        </div>
      )}

      {!loading && templates.length > 0 && (
        <div style={{ display:'grid', gridTemplateColumns:'280px 1fr', gap:16 }}>

          {/* Template list */}
          <div>
            {templates.map(t => (
              <div key={t.id}
                onClick={()=>setSelected(t)}
                style={{ background: selected?.id===t.id ? '#111830' : '#252838',
                  border: `1px solid ${selected?.id===t.id ? '#f97316' : '#2e3150'}`,
                  borderRadius:10, padding:'12px 14px', marginBottom:8, cursor:'pointer' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:4 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:'#e2e8f0' }}>{t.name}</div>
                  {t.is_default && <span style={{ fontSize:9, background:'#f97316', color:'#fff', borderRadius:4, padding:'1px 6px', fontWeight:700 }}>DEFAULT</span>}
                </div>
                <div style={{ fontSize:11, color:'#4a5568' }}>{t.file_name}</div>
                <div style={{ display:'flex', gap:8, marginTop:6 }}>
                  {t.is_active && <span style={{ fontSize:9, background:'#1a3a1a', color:'#4ade80', borderRadius:4, padding:'1px 6px', fontWeight:700 }}>ACTIVE</span>}
                  <span style={{ fontSize:9, background:'#2e3150', color:'#64748b', borderRadius:4, padding:'1px 6px' }}>v{t.version}</span>
                  {t.requires_signature && <span style={{ fontSize:9, background:'#1e2a4a', color:'#60a5fa', borderRadius:4, padding:'1px 6px' }}>SIG REQUIRED</span>}
                </div>
              </div>
            ))}
          </div>

          {/* Template detail */}
          {selected && (
            <div>
              <div style={S.card}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16 }}>
                  <div>
                    <div style={{ fontSize:16, fontWeight:700, color:'#e2e8f0', marginBottom:4 }}>{selected.name}</div>
                    {selected.description && <div style={{ fontSize:12, color:'#64748b' }}>{selected.description}</div>}
                  </div>
                  <div style={{ display:'flex', gap:8 }}>
                    <button onClick={()=>setShowSend(true)} style={S.btn('#1e3a5f')}>📧 Send to Client</button>
                    <a href={selected.file_url} target="_blank" rel="noopener noreferrer"
                      style={{ ...S.btn('#14532d'), textDecoration:'none', display:'inline-block' }}>
                      👁 View PDF
                    </a>
                  </div>
                </div>

                <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:16 }}>
                  {[
                    { label:'File', val: selected.file_name },
                    { label:'Size', val: fmtSize(selected.file_size||0) },
                    { label:'Version', val: `v${selected.version}` },
                    { label:'Uploaded', val: fmtDate(selected.created_at) },
                  ].map(m => (
                    <div key={m.label} style={{ background:'#1e2235', borderRadius:7, padding:'10px 12px' }}>
                      <div style={{ fontSize:10, color:'#4a5568', textTransform:'uppercase', marginBottom:2 }}>{m.label}</div>
                      <div style={{ fontSize:12, color:'#e2e8f0', fontWeight:600, wordBreak:'break-all' }}>{m.val}</div>
                    </div>
                  ))}
                </div>

                {/* Actions */}
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  <button onClick={()=>replRef.current?.click()} disabled={replacing===selected.id}
                    style={{ ...S.ghost, color:'#f97316', borderColor:'#f97316' }}>
                    {replacing===selected.id ? '⏳ Replacing…' : '🔄 Replace File (new version)'}
                  </button>
                  <input ref={replRef} type="file" accept=".pdf,.doc,.docx" style={{ display:'none' }}
                    onChange={e=>{ if(e.target.files[0]) replaceFile(selected, e.target.files[0]); e.target.value=''; }} />

                  <button onClick={()=>toggleDefault(selected)} style={S.ghost}>
                    {selected.is_default ? '⭐ Remove as Default' : '☆ Set as Default'}
                  </button>

                  <button onClick={()=>deleteTemplate(selected.id)}
                    style={{ ...S.ghost, color:'#f87171', borderColor:'#3b0a0a' }}>
                    🗑 Delete
                  </button>
                </div>
              </div>

              {/* Send history */}
              <div style={S.card}>
                <div style={{ fontSize:13, fontWeight:700, color:'#e2e8f0', marginBottom:14 }}>
                  📬 Send History
                  <span style={{ fontSize:11, color:'#4a5568', fontWeight:400, marginLeft:8 }}>{sends.length} sends</span>
                </div>
                {loadingSends && <div style={{ color:'#4a5568', fontSize:12, textAlign:'center', padding:16 }}>Loading…</div>}
                {!loadingSends && sends.length === 0 && (
                  <div style={{ color:'#3a3f60', fontSize:12, textAlign:'center', padding:20 }}>Not sent yet</div>
                )}
                {!loadingSends && sends.length > 0 && (
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead><tr>
                      {['Sent To','Email','Sent At','Status'].map(h=>(
                        <th key={h} style={{ padding:'6px 10px', textAlign:'left', fontSize:10, color:'#4a5568', fontWeight:600, borderBottom:'1px solid #2e3150', textTransform:'uppercase' }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {sends.map(s => (
                        <tr key={s.id} onMouseEnter={e=>e.currentTarget.style.background='#1e2235'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                          <td style={{ padding:'8px 10px', fontSize:12, color:'#e2e8f0' }}>{s.sent_to_name||'—'}</td>
                          <td style={{ padding:'8px 10px', fontSize:12, color:'#4a5568' }}>{s.sent_to_email}</td>
                          <td style={{ padding:'8px 10px', fontSize:11, color:'#64748b' }}>{fmtDate(s.sent_at)}</td>
                          <td style={{ padding:'8px 10px' }}>
                            <span style={{ fontSize:10, fontWeight:700,
                              background: s.status==='signed'?'#1a3a1a':s.status==='viewed'?'#1e3a5f':'#2e3150',
                              color: s.status==='signed'?'#4ade80':s.status==='viewed'?'#60a5fa':'#64748b',
                              borderRadius:4, padding:'2px 8px', textTransform:'capitalize' }}>
                              {s.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── UPLOAD MODAL ── */}
      {showUpload && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:3000, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={e=>{ if(e.target===e.currentTarget) setShowUpload(false); }}>
          <div style={{ background:'#252838', border:'1px solid #3a3f60', borderRadius:14, padding:28, width:440, boxShadow:'0 16px 48px rgba(0,0,0,0.6)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <div style={{ fontSize:15, fontWeight:700, color:'#e2e8f0' }}>Upload Business Document</div>
              <button onClick={()=>setShowUpload(false)} style={{ background:'none', border:'none', color:'#4a5568', cursor:'pointer', fontSize:18 }}>✕</button>
            </div>

            <div style={{ marginBottom:16 }}>
              <label style={S.lbl}>Contract Name *</label>
              <input style={S.inp} placeholder="e.g. Standard Rental Agreement 2025" value={upForm.name} onChange={e=>setUpForm(f=>({...f,name:e.target.value}))} autoFocus />
            </div>

            <div style={{ marginBottom:16 }}>
              <label style={S.lbl}>Description</label>
              <input style={S.inp} placeholder="Optional description" value={upForm.description} onChange={e=>setUpForm(f=>({...f,description:e.target.value}))} />
            </div>

            {/* File drop */}
            <div onClick={()=>fileRef.current?.click()}
              style={{ border:`2px dashed ${upFile?'#f97316':'#3a3f60'}`, borderRadius:8, padding:'20px', textAlign:'center', cursor:'pointer', marginBottom:16,
                background: upFile?'#1a0e00':'#1e2235' }}>
              {upFile ? (
                <div>
                  <div style={{ fontSize:13, fontWeight:700, color:'#f97316' }}>📄 {upFile.name}</div>
                  <div style={{ fontSize:11, color:'#4a5568', marginTop:4 }}>{fmtSize(upFile.size)} — click to change</div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize:22, marginBottom:6 }}>📁</div>
                  <div style={{ fontSize:13, color:'#4a5568' }}>Click to select PDF or Word document</div>
                  <div style={{ fontSize:11, color:'#3a3f60', marginTop:4 }}>PDF, DOC, DOCX — max 20MB</div>
                </div>
              )}
              <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" style={{ display:'none' }}
                onChange={e=>{ if(e.target.files[0]) setUpFile(e.target.files[0]); }} />
            </div>

            <div style={{ display:'flex', gap:16, marginBottom:16 }}>
              <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer' }}>
                <input type="checkbox" checked={upForm.is_default} onChange={e=>setUpForm(f=>({...f,is_default:e.target.checked}))} style={{ accentColor:'#f97316' }} />
                <span style={{ fontSize:12, color:'#94a3b8' }}>Set as default</span>
              </label>
              <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer' }}>
                <input type="checkbox" checked={upForm.requires_signature} onChange={e=>setUpForm(f=>({...f,requires_signature:e.target.checked}))} style={{ accentColor:'#f97316' }} />
                <span style={{ fontSize:12, color:'#94a3b8' }}>Requires signature</span>
              </label>
            </div>

            <div style={{ display:'flex', gap:10 }}>
              <button onClick={()=>setShowUpload(false)} style={{ ...S.ghost, flex:1 }}>Cancel</button>
              <button onClick={uploadTemplate} disabled={uploading||!upFile||!upForm.name.trim()}
                style={{ ...S.btn(), flex:2, opacity: uploading||!upFile||!upForm.name.trim() ? 0.5 : 1 }}>
                {uploading ? '⏳ Uploading…' : '📤 Upload Document'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── SEND MODAL ── */}
      {showSend && selected && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:3000, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={e=>{ if(e.target===e.currentTarget) setShowSend(false); }}>
          <div style={{ background:'#252838', border:'1px solid #3a3f60', borderRadius:14, padding:28, width:420, boxShadow:'0 16px 48px rgba(0,0,0,0.6)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
              <div style={{ fontSize:15, fontWeight:700, color:'#e2e8f0' }}>📧 Send Contract</div>
              <button onClick={()=>setShowSend(false)} style={{ background:'none', border:'none', color:'#4a5568', cursor:'pointer', fontSize:18 }}>✕</button>
            </div>
            <div style={{ fontSize:12, color:'#4a5568', marginBottom:16 }}>Sending: <span style={{ color:'#f97316' }}>{selected.name}</span></div>

            <label style={S.lbl}>Send to Client</label>
            <select style={{ ...S.inp, marginBottom:10 }} value={sendForm.client_id}
              onChange={e=>{ const c=clients.find(x=>String(x.id)===e.target.value); setSendForm(f=>({...f,client_id:e.target.value,custom_email:c?.email||'',custom_name:c?.name||''})); }}>
              <option value="">— Select client or enter manually —</option>
              {clients.filter(c=>c.email).map(c=>(
                <option key={c.id} value={c.id}>{c.name} {c.company?`(${c.company})`:''} — {c.email}</option>
              ))}
            </select>

            <div style={{ textAlign:'center', color:'#3a3f60', fontSize:11, marginBottom:10 }}>— or enter manually —</div>

            <label style={S.lbl}>Email *</label>
            <input style={{ ...S.inp, marginBottom:10 }} placeholder="client@email.com" value={sendForm.custom_email}
              onChange={e=>setSendForm(f=>({...f,custom_email:e.target.value,client_id:''}))} />

            <label style={S.lbl}>Recipient Name</label>
            <input style={{ ...S.inp, marginBottom:10 }} placeholder="John Smith" value={sendForm.custom_name}
              onChange={e=>setSendForm(f=>({...f,custom_name:e.target.value}))} />

            <label style={S.lbl}>Custom Message (optional)</label>
            <textarea style={{ ...S.inp, height:70, resize:'none', fontFamily:'inherit', marginBottom:16 }}
              placeholder="Please review and sign this rental agreement before your pickup date…"
              value={sendForm.message} onChange={e=>setSendForm(f=>({...f,message:e.target.value}))} />

            <div style={{ display:'flex', gap:10 }}>
              <button onClick={()=>setShowSend(false)} style={{ ...S.ghost, flex:1 }}>Cancel</button>
              <button onClick={sendContract} disabled={sending||(!sendForm.client_id&&!sendForm.custom_email)}
                style={{ ...S.btn(), flex:2, opacity: sending||(!sendForm.client_id&&!sendForm.custom_email)?0.5:1 }}>
                {sending ? '⏳ Sending…' : '📧 Send Contract'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── SEND CURRENT RENTAL TERMS MODAL ── */}
      {showSendTerms && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:3000, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={e=>{ if(e.target===e.currentTarget) setShowSendTerms(false); }}>
          <div style={{ background:'#252838', border:'1px solid #3a3f60', borderRadius:14, padding:28, width:420, boxShadow:'0 16px 48px rgba(0,0,0,0.6)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
              <div style={{ fontSize:15, fontWeight:700, color:'#e2e8f0' }}>📧 Send Current Rental Terms</div>
              <button onClick={()=>setShowSendTerms(false)} style={{ background:'none', border:'none', color:'#4a5568', cursor:'pointer', fontSize:18 }}>✕</button>
            </div>
            <div style={{ fontSize:12, color:'#4a5568', marginBottom:16 }}>Sends whatever is currently set in <span style={{ color:'#f97316' }}>Customer Rental Terms</span> above — not a document uploaded here.</div>

            <label style={S.lbl}>Send to Client</label>
            <select style={{ ...S.inp, marginBottom:10 }} value={termsSendForm.client_id}
              onChange={e=>{ const c=clients.find(x=>String(x.id)===e.target.value); setTermsSendForm(f=>({...f,client_id:e.target.value,custom_email:c?.email||'',custom_name:c?.name||''})); }}>
              <option value="">— Select client or enter manually —</option>
              {clients.filter(c=>c.email).map(c=>(
                <option key={c.id} value={c.id}>{c.name} {c.company?`(${c.company})`:''} — {c.email}</option>
              ))}
            </select>

            <div style={{ textAlign:'center', color:'#3a3f60', fontSize:11, marginBottom:10 }}>— or enter manually —</div>

            <label style={S.lbl}>Email *</label>
            <input style={{ ...S.inp, marginBottom:10 }} placeholder="client@email.com" value={termsSendForm.custom_email}
              onChange={e=>setTermsSendForm(f=>({...f,custom_email:e.target.value,client_id:''}))} />

            <label style={S.lbl}>Recipient Name</label>
            <input style={{ ...S.inp, marginBottom:10 }} placeholder="John Smith" value={termsSendForm.custom_name}
              onChange={e=>setTermsSendForm(f=>({...f,custom_name:e.target.value}))} />

            <label style={S.lbl}>Custom Message (optional)</label>
            <textarea style={{ ...S.inp, height:70, resize:'none', fontFamily:'inherit', marginBottom:16 }}
              placeholder="Please review our current rental terms…"
              value={termsSendForm.message} onChange={e=>setTermsSendForm(f=>({...f,message:e.target.value}))} />

            <div style={{ display:'flex', gap:10 }}>
              <button onClick={()=>setShowSendTerms(false)} style={{ ...S.ghost, flex:1 }}>Cancel</button>
              <button onClick={sendCurrentTerms} disabled={sendingTerms||(!termsSendForm.client_id&&!termsSendForm.custom_email)}
                style={{ ...S.btn(), flex:2, opacity: sendingTerms||(!termsSendForm.client_id&&!termsSendForm.custom_email)?0.5:1 }}>
                {sendingTerms ? '⏳ Sending…' : '📧 Send Rental Terms'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
